import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { TranslocoModule } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  AccountPublicApiService
} from '@/app/platform/platform-core/user/services/account-public-api.service';
import {
  isPasswordAcceptable,
  MINIMUM_PASSWORD_LENGTH,
  PasswordStrengthMeterComponent
} from '@/app/platform/platform-core/user/components/password-strength-meter/password-strength-meter.component';
import { PublicAccountShellComponent } from '../public-account-shell/public-account-shell.component';

type ResetState = 'form' | 'success' | 'failed' | 'missingToken';

/** Reset-pagina via /reset-password?token=...: nieuw wachtwoord met sterkte-meter. */
@Component({
  selector: 'reset-password-page',
  imports: [
    FormsModule,
    RouterLink,
    TranslocoModule,
    SharedModule,
    PublicAccountShellComponent,
    PasswordStrengthMeterComponent
  ],
  templateUrl: './reset-password-page.component.html',
  styleUrl: './reset-password-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ResetPasswordPageComponent implements OnInit {
  private readonly accountApi = inject(AccountPublicApiService);
  private readonly route = inject(ActivatedRoute);

  protected readonly minimumPasswordLength = MINIMUM_PASSWORD_LENGTH;

  protected readonly state = signal<ResetState>('form');
  protected readonly isSubmitting = signal(false);

  protected readonly password = signal('');
  protected readonly confirmPassword = signal('');

  private resetToken = '';

  protected readonly passwordIsAcceptable = computed(() => isPasswordAcceptable(this.password()));
  protected readonly passwordsMatch = computed(
    () => this.confirmPassword() === '' || this.password() === this.confirmPassword()
  );

  protected readonly canSubmit = computed(
    () =>
      !this.isSubmitting() &&
      this.passwordIsAcceptable() &&
      this.password() === this.confirmPassword()
  );

  ngOnInit(): void {
    this.resetToken = this.route.snapshot.queryParamMap.get('token') ?? '';
    if (!this.resetToken) {
      this.state.set('missingToken');
    }
  }

  async onSubmit(): Promise<void> {
    if (!this.canSubmit()) {
      return;
    }

    this.isSubmitting.set(true);
    const result = await this.accountApi.resetPassword(
      this.resetToken,
      this.password(),
      this.confirmPassword()
    );
    this.isSubmitting.set(false);

    result.ifSuccess(reset => this.state.set(reset ? 'success' : 'failed'));
    result.ifError(() => this.state.set('failed'));
  }
}
